// Progression d'une série : son 1RM estimé comparé à celui de la série de
// même rang de la dernière occurrence de l'exercice.
//
// Même règle que l'app (gymnote/core/progressionSerie.js) et que l'API
// (Parametrage/CoachRegles/ComparaisonSerie.cs), pour que le coach et l'élève
// lisent les mêmes progressions :
// - une série sans reps (pas encore faite) ne se compare pas et ne compte pas
//   dans le score ;
// - le 1RM estimé suit Epley, charge × (1 + reps / 30), avec les reps laissées
//   en réserve quand le RPE est saisi (10 − RPE) : 7 × 20 kg à RPE 8 valent
//   9 × 20 kg ;
// - au poids du corps (0 kg des deux côtés), Epley vaut 0 : on compare les
//   reps équivalentes, ce qui revient au même à charge constante ;
// - sur un exercice d'assistance, la charge est l'aide reçue et son 1RM ne
//   mesure aucune force. Progresser, c'est moins d'aide sans perdre de reps,
//   ou plus de reps sans plus d'aide. Moins d'aide pour moins de reps reste
//   stable.
//
// Score de progression = séries en progression / séries comparées.

import { effectiveWeight, formatWeight } from "./weight";

export const PROGRESSION = 1;
export const STABLE = 0;
export const REGRESSION = -1;

// Écarts de calcul flottant : en deçà, deux valeurs sont égales.
const EPSILON = 1e-6;

// Reps « équivalentes » : un RPE lisible entre 1 et 10 ajoute les reps en
// réserve. RPE absent, illisible ou hors échelle (dont le 0 des anciennes
// saisies) : les reps réelles. Virgule acceptée.
export const repsEquivalentes = (reps, rpe) => {
	const r = Number(reps) || 0;
	if (rpe == null || String(rpe).trim() === "") return r;
	// Chiffres et une décimale seulement (« 0x9 » ou « 9e0 » passeraient Number) :
	// même expression que l'API (Tendance.cs, RepsEquivalentes).
	const texte = String(rpe).trim().replace(",", ".");
	if (!/^[0-9]+(\.[0-9]+)?$/.test(texte)) return r;
	const valeur = Number(texte);
	if (valeur < 1 || valeur > 10) return r;
	return r + (10 - valeur);
};

// Epley : charge × (1 + reps / 30), reps en réserve comprises.
export const unRmEstime = (charge, reps, rpe) =>
	(Number(charge) || 0) * (1 + repsEquivalentes(reps, rpe) / 30);

const sens = (valeur, reference) =>
	valeur > reference + EPSILON ? PROGRESSION : valeur < reference - EPSILON ? REGRESSION : STABLE;

// Séries au format du site : { reps, poids, poidsDec, rpe }.
// Renvoie PROGRESSION, STABLE, REGRESSION, ou null si la paire ne se compare pas.
export const comparerSeries = (actuelle, precedente, estAssistance = false) => {
	if (!actuelle || !precedente) return null;
	const reps = parseInt(actuelle.reps) || 0;
	const repsPrecedentes = parseInt(precedente.reps) || 0;
	if (reps <= 0 || repsPrecedentes <= 0) return null;

	const equivalentes = repsEquivalentes(reps, actuelle.rpe);
	const equivalentesPrecedentes = repsEquivalentes(repsPrecedentes, precedente.rpe);
	const charge = effectiveWeight(actuelle.poids, actuelle.poidsDec);
	const chargePrecedente = effectiveWeight(precedente.poids, precedente.poidsDec);

	if (estAssistance) {
		const coteReps = sens(equivalentes, equivalentesPrecedentes);
		// Moins d'aide = mieux : la valeur et la référence sont inversées.
		const coteAide = sens(chargePrecedente, charge);
		if (coteReps >= 0 && coteAide >= 0 && coteReps + coteAide > 0) return PROGRESSION;
		if (coteReps <= 0 && coteAide <= 0 && coteReps + coteAide < 0) return REGRESSION;
		return STABLE;
	}

	if (charge <= 0 && chargePrecedente <= 0) return sens(equivalentes, equivalentesPrecedentes);

	return sens(
		unRmEstime(charge, reps, actuelle.rpe),
		unRmEstime(chargePrecedente, repsPrecedentes, precedente.rpe)
	);
};

export const creerBilan = () => ({ progressees: 0, regressees: 0, comparees: 0 });

// Ajoute au bilan chaque série appariée par rang à la série précédente.
export const ajouterSeries = (bilan, seriesActuelles, seriesPrecedentes, estAssistance = false) => {
	(seriesActuelles || []).forEach((serie, index) => {
		const resultat = comparerSeries(serie, seriesPrecedentes?.[index], estAssistance);
		if (resultat == null) return;
		bilan.comparees += 1;
		if (resultat === PROGRESSION) bilan.progressees += 1;
		else if (resultat === REGRESSION) bilan.regressees += 1;
	});
	return bilan;
};

// Pourcentages entiers du bilan, null si aucune série n'a été comparée.
export const pourcentagesBilan = (bilan) => {
	if (!bilan || bilan.comparees <= 0) return null;
	return {
		progressionPct: Math.round((100 * bilan.progressees) / bilan.comparees),
		regressionPct: Math.round((100 * bilan.regressees) / bilan.comparees),
	};
};

// Max de départ proposé au coach : le 1RM estimé arrondi à 2,5 kg, comme les
// charges calculées à l'affectation.
export const arrondiMax = (unRm) => {
	const v = Number(unRm) || 0;
	if (v <= 0) return 0;
	return Math.round(v / 2.5) * 2.5;
};

// Variation d'une série comparée à la précédente, en % et toujours positive :
// le sens vient de la liste où elle s'affiche (progressions ou régressions).
// Celle du 1RM estimé, ou des reps équivalentes au poids du corps. Null si
// rien ne se mesure.
export const variationSerie = (actuelle, precedente) => {
	if (!actuelle || !precedente) return null;
	const charge = effectiveWeight(actuelle.poids, actuelle.poidsDec);
	const chargePrecedente = effectiveWeight(precedente.poids, precedente.poidsDec);
	const auPoidsDuCorps = charge <= 0 && chargePrecedente <= 0;
	const valeur = auPoidsDuCorps
		? repsEquivalentes(actuelle.reps, actuelle.rpe)
		: unRmEstime(charge, actuelle.reps, actuelle.rpe);
	const reference = auPoidsDuCorps
		? repsEquivalentes(precedente.reps, precedente.rpe)
		: unRmEstime(chargePrecedente, precedente.reps, precedente.rpe);
	if (!(reference > 0) || !((parseInt(precedente.reps) || 0) > 0)) return null;
	return Math.abs((valeur - reference) / reference) * 100;
};

// RPE à afficher, virgule française : seulement s'il compte dans le 1RM (lisible,
// de 1 à 10). Null sinon : « 0 » des anciennes saisies, vide, hors échelle.
export const rpeLisible = (rpe) => {
	if (rpe == null) return null;
	const texte = String(rpe).trim().replace(",", ".");
	if (!/^[0-9]+(\.[0-9]+)?$/.test(texte)) return null;
	const valeur = Number(texte);
	if (valeur < 1 || valeur > 10) return null;
	return String(valeur).replace(".", ",");
};

// « 8 × 80,25 kg · RPE 8,5 » : le RPE n'apparaît que s'il est lisible (1 à 10).
export const libelleSerie = (serie) => {
	const reps = parseInt(serie?.reps) || 0;
	let libelle = `${reps} × ${formatWeight(serie?.poids, serie?.poidsDec)} kg`;
	const rpe = rpeLisible(serie?.rpe);
	if (rpe) libelle += ` · RPE ${rpe}`;
	return libelle;
};
